import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { KeyRound, Plus, Copy, XCircle, ShieldCheck } from "lucide-react";

export default function AdminCodes() {
  const { user } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  const [checked, setChecked] = useState(false);
  const [codes, setCodes] = useState([]);
  const [hint, setHint] = useState("");
  const [count, setCount] = useState(1);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user) return;
    api.get("/admin/whoami").then((r) => { setIsAdmin(!!r.data?.is_admin); setChecked(true); }).catch(() => setChecked(true));
  }, [user]);

  const load = () => api.get("/admin/codes").then((r) => setCodes(r.data)).catch(() => {});
  useEffect(() => { if (isAdmin) load(); }, [isAdmin]);

  if (!user) return <Navigate to="/login" />;
  if (checked && !isAdmin) {
    return (
      <div className="mx-auto max-w-lg px-6 py-24">
        <div className="mono text-xs text-primary">// admin · access denied</div>
        <h1 className="mt-2 text-4xl font-black tracking-tighter">Admins only.</h1>
        <p className="mt-3 text-sm text-muted-foreground">This panel is restricted to accounts listed in the <span className="mono">ADMIN_EMAILS</span> environment variable.</p>
      </div>
    );
  }

  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/codes", { company_hint: hint, count });
      toast.success(`${data.length} code(s) created`);
      setHint(""); setCount(1);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const revoke = async (code_id) => {
    await api.post("/admin/codes/revoke", { code_id }).catch(() => {});
    load();
  };

  const copy = (code) => { navigator.clipboard.writeText(code); toast.success("Copied"); };

  return (
    <div className="mx-auto max-w-5xl px-6 py-16">
      <div className="mono text-xs text-primary flex items-center gap-2"><ShieldCheck className="h-3 w-3" />admin · portal codes</div>
      <h1 className="mt-2 text-5xl font-black tracking-tighter">Company access codes.</h1>
      <p className="mt-3 max-w-2xl text-muted-foreground">Issue single-use reference codes per organisation. Each code upgrades exactly one account to a company account. Revoke any time.</p>

      <div className="mt-8 grid gap-8 lg:grid-cols-[1fr_2fr]">
        <div className="border border-primary/40 p-6 space-y-4 h-fit">
          <div className="flex items-center gap-2 text-primary text-xs uppercase tracking-widest"><Plus className="h-3 w-3" />Issue codes</div>
          <div><Label className="text-xs uppercase tracking-widest text-muted-foreground">Company hint (optional)</Label><Input data-testid="code-hint" value={hint} onChange={(e) => setHint(e.target.value)} className="mt-2 rounded-none bg-input" placeholder="e.g. Green Foundry" /></div>
          <div><Label className="text-xs uppercase tracking-widest text-muted-foreground">Count</Label><Input data-testid="code-count" type="number" min="1" max="50" value={count} onChange={(e) => setCount(parseInt(e.target.value || "1", 10))} className="mt-2 rounded-none bg-input" /></div>
          <Button onClick={create} disabled={busy} data-testid="code-create" className="w-full rounded-none bg-primary text-primary-foreground hover:bg-primary/90"><KeyRound className="mr-2 h-4 w-4" />{busy ? "Generating…" : "Generate"}</Button>
        </div>

        <div>
          <div className="mb-3 text-xs uppercase tracking-widest text-muted-foreground">All codes ({codes.length})</div>
          <div className="border border-border">
            {codes.length === 0 ? (
              <div className="p-10 text-center text-sm text-muted-foreground">No codes yet.</div>
            ) : codes.map((c) => (
              <div key={c.id} data-testid={`code-row-${c.id}`} className={`flex items-center gap-3 p-3 border-b border-border last:border-b-0 ${c.revoked ? "opacity-50" : ""}`}>
                <button onClick={() => copy(c.code)} className="mono text-primary hover:underline flex items-center gap-1"><Copy className="h-3 w-3" />{c.code}</button>
                <div className="flex-1 text-xs">
                  <div>{c.company_hint || <span className="text-muted-foreground italic">no hint</span>}</div>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                    {c.revoked ? "revoked" : c.used_by ? `used ${new Date(c.used_at).toLocaleDateString()}` : "available"}
                  </div>
                </div>
                {!c.revoked && !c.used_by && (
                  <button data-testid={`revoke-${c.id}`} onClick={() => revoke(c.id)} className="text-muted-foreground hover:text-destructive"><XCircle className="h-4 w-4" /></button>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
